import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import PortalModal from 'components/Modals/PortalModal';
import { withRouter, useHistory } from 'react-router-dom';
import usePlanServiceApi from 'hooks/usePlanServiceApi';
import catchCancel from 'helpers/catchCancel';
import { Context } from 'components/Store';
import { parseServerError } from 'helpers/errorHelpers';
import { paths } from 'routes/paths';
import {
  PLAN_DELETE_V2,
  PLAN_DELETE_V2_BY_ORG
} from 'constants/planServiceV2Flags';

const DeletePlanModal = ({
  open,
  close,
  planName = '',
  planId = '',
  onDeleted
}) => {
  const plansApi = usePlanServiceApi(
    'plan',
    PLAN_DELETE_V2,
    PLAN_DELETE_V2_BY_ORG
  );
  const [, dispatch] = useContext(Context);
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const handleDelete = async () => {
    setIsSaving(true);
    const { promise } = plansApi.delete(planId);

    await promise
      .then(() => {
        // Close explicitly and notify the caller before navigating: callers
        // that stay on the same route after delete (e.g. the redesigned
        // inline Plans detail panel) have no route change to unmount this
        // modal or refresh their list.
        close();
        onDeleted?.(planId);
        // The Plans landing page already renders this route, so pushing it
        // again only stacks a duplicate history entry.
        if (history.location.pathname !== paths.plans) {
          history.push({
            pathname: paths.plans
          });
        }
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      // List callers keep this modal mounted after `close()`, so a saving flag
      // left set here disables the Delete button on every later open.
      .finally(() => setIsSaving(false));
  };

  const handleCancel = () => {
    close();
  };

  const actionPerformed = 'Delete "';
  const closeQuote = '"';

  return (
    <PortalModal
      open={open}
      title={actionPerformed + planName + closeQuote}
      close={close}
      cancelLabel="Cancel"
      confirmLabel="Delete"
      onCancel={() => handleCancel()}
      type="error"
      onConfirm={() => handleDelete()}
      isSaving={isSaving}
    >
      <div className="pr-6">
        You are about to permanently delete this plan. This cannot be undone.
        Are you sure you want to proceed?
      </div>
    </PortalModal>
  );
};

DeletePlanModal.propTypes = {
  open: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]).isRequired,
  close: PropTypes.func.isRequired,
  planName: PropTypes.string,
  planId: PropTypes.string,
  onDeleted: PropTypes.func
};

export default withRouter(DeletePlanModal);
